import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Input from '../ui/Input';
import Button from '../ui/Button';
import ConfirmDialog from '../ui/ConfirmDialog';
import AdminEmailAutocomplete from './AdminEmailAutocomplete';
import { useToast } from '../ui/Toast';
import { authErrorMessage } from '../../lib/authErrorMessage';
import { uuidActionSchema } from '../../lib/validators/adminSchemas';

/**
 * "Pick an account, run one action" form — shared by the super-admin
 * Manage Administrators screen (grant access, unlock) and the plain-admin
 * Unlock Account page.
 *
 * `lookup="admins"` turns on the type-to-search email/name picker
 * (AdminEmailAutocomplete), sourced from the live GET /admin/admins list —
 * the only real endpoint anywhere that returns {email, uuid} pairs. That
 * only covers admin-role targets though: there's no endpoint yet listing
 * investor accounts (Investors is still "not yet populated"), and
 * make-admin's whole point is targeting accounts that AREN'T admins yet —
 * so neither of those has a real directory to search. A "Enter UUID
 * manually" toggle covers those cases without inventing search data that
 * doesn't exist.
 *
 * `confirmMessage`, when passed, gates the run behind a ConfirmDialog
 * instead of firing immediately — used for make-admin, since granting
 * access force-logs-out the target's existing session (not used for
 * unlock, which is idempotent and safe to fire straight away).
 */
export default function UuidActionForm({
  actionLabel,
  helperText,
  onRun,
  isLoading,
  successMessage,
  notFoundMessage,
  confirmTitle,
  confirmMessage,
  lookup,
}) {
  const toast = useToast();
  const [pendingUuid, setPendingUuid] = useState(null);
  const [manualMode, setManualMode] = useState(!lookup);
  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm({ resolver: zodResolver(uuidActionSchema), defaultValues: { uuid: '' } });

  const runAction = async (uuid) => {
    try {
      await onRun(uuid).unwrap();
      toast.push({ tone: 'success', message: successMessage });
      reset({ uuid: '' });
    } catch (err) {
      setError('root', { message: authErrorMessage(err, { notFound: notFoundMessage, fallback: 'That action failed.' }) });
    } finally {
      setPendingUuid(null);
    }
  };

  const onSubmit = ({ uuid }) => {
    if (confirmMessage) {
      setPendingUuid(uuid);
      return;
    }
    runAction(uuid);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
      {lookup && !manualMode ? (
        <Controller
          name="uuid"
          control={control}
          render={({ field }) => (
            <AdminEmailAutocomplete value={field.value} onChange={field.onChange} error={errors.uuid?.message} label="Administrator" />
          )}
        />
      ) : (
        <Input label="Account UUID" placeholder="e.g. 3f9a1c2e-..." error={errors.uuid?.message} {...register('uuid')} />
      )}

      {lookup && (
        <button
          type="button"
          onClick={() => setManualMode((m) => !m)}
          className="text-xs text-accent hover:underline"
        >
          {manualMode ? 'Search administrators by email instead' : "Not an admin account? Enter a UUID manually"}
        </button>
      )}

      {errors.root && <p className="text-sm text-danger">{errors.root.message}</p>}
      {helperText && <p className="text-xs text-foreground-subtle">{helperText}</p>}
      <Button type="submit" variant="secondary" size="sm" isLoading={isLoading}>
        {actionLabel}
      </Button>

      {confirmMessage && (
        <ConfirmDialog
          open={!!pendingUuid}
          onClose={() => setPendingUuid(null)}
          onConfirm={() => runAction(pendingUuid)}
          isLoading={isLoading}
          tone="secondary"
          title={confirmTitle || 'Are you sure?'}
          confirmLabel={actionLabel}
          message={confirmMessage}
        />
      )}
    </form>
  );
}
